import DOMPurify from "dompurify";

// Strict HTML sanitizer for markup fetched from the Sefaria API.
// -> This is a security control: it prevents XSS from third-party,
//    compromised, or malformed markup before we inject it into the DOM.

// Only these tags survive sanitization; everything else (script, img,
// style, etc.) is dropped.
const ALLOWED_TAGS = ["b", "i", "sup", "sub", "span", "a", "br", "small"];

// Only these attributes survive; event handlers (onerror, onclick, ...), class
// names, and all other attributes are dropped. Class is intentionally excluded:
// upstream Sefaria markup must not be able to borrow app-owned CSS selectors.
const ALLOWED_ATTR = ["href", "lang", "dir"];

// Hosts we trust for absolute https links.
const ALLOWED_HOSTS = new Set(["sefaria.org", "www.sefaria.org"]);

// Decide what to do with an href.
// -> "cite": a RELATIVE path link ("/Chagigah.12a.3") - Sefaria's in-text
//    citation form. Kept as an inert data-mm-cite attribute (href REMOVED):
//    a live relative href would navigate the SPA host to a 404, and a "#..."
//    href would mutate the app's hash router. The navigator's citation
//    resolver reads data-mm-cite, gates the ref against this source's real
//    links, and only then wires an in-app peek.
// -> "external": https + allowlisted host - kept, hardened for a new tab.
// -> "drop": everything else (including "#..." fragments).
function classifyHref(href: string): "cite" | "external" | "drop" {
  if (href.startsWith("/")) return "cite";
  if (href.startsWith("#")) return "drop";
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    // -> Not parseable as an absolute URL and not a known relative form.
    return "drop";
  }
  if (url.protocol === "https:" && ALLOWED_HOSTS.has(url.hostname)) {
    return "external";
  }
  return "drop";
}

// Enforce the href allowlist on every <a> after attribute sanitization.
// -> Keep the anchor text either way; only the href (and target/rel) change.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName !== "A") {
    return;
  }
  const anchor = node as HTMLAnchorElement;
  if (!anchor.hasAttribute("href")) {
    return;
  }
  const href = anchor.getAttribute("href") ?? "";
  const kind = classifyHref(href);
  if (kind === "drop") {
    // -> Drop the href but leave the anchor (and its text) in place.
    anchor.removeAttribute("href");
    anchor.removeAttribute("target");
    anchor.removeAttribute("rel");
    return;
  }
  if (kind === "cite") {
    // -> Neutralize navigation; preserve the path for the citation resolver.
    //    (Added AFTER attribute sanitization, so ALLOW_DATA_ATTR:false cannot
    //    be spoofed by incoming markup - inbound data-* was already stripped.)
    anchor.removeAttribute("href");
    anchor.removeAttribute("target");
    anchor.removeAttribute("rel");
    anchor.setAttribute("data-mm-cite", href);
    return;
  }
  // -> Harden trusted external links against tab-nabbing.
  anchor.setAttribute("rel", "noopener noreferrer");
  anchor.setAttribute("target", "_blank");
});

// Bounded LRU memo for sanitizeSefariaHtml: the same source HTML is sanitized 3-4x
// per open (hidden engine body, each reader fill, Explore panel rebuilds). Sound
// because the output is a pure function of the input string: the config below is a
// constant, and the hook above reads only the node plus module constants
// (classifyHref parses with new URL(href) and no base, so nothing depends on
// location, time, or mutable state). Only the SANITIZED string is ever stored, so
// a hit can never hand back raw markup. Very large inputs skip the memo.
const MEMO_MAX_ENTRIES = 64;
const MEMO_MAX_INPUT_CHARS = 200000;
const sanitizeMemo = new Map<string, string>();

// Sanitize raw Sefaria HTML down to the allowed tag/attribute set.
export function sanitizeSefariaHtml(html: string): string {
  const hit = sanitizeMemo.get(html);
  if (hit !== undefined) {
    // -> Re-insert so Map order tracks recency (oldest key first).
    sanitizeMemo.delete(html);
    sanitizeMemo.set(html, hit);
    return hit;
  }
  // ALLOW_DATA_ATTR / ALLOW_ARIA_ATTR default to true and would BYPASS the
  // ALLOWED_ATTR allowlist; deny them so injected data-*/aria-* can't spoof our
  // DOM-derived trail or pollute the accessibility tree (heavy-review fix SEC-1).
  const out = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });
  if (html.length <= MEMO_MAX_INPUT_CHARS) {
    if (sanitizeMemo.size >= MEMO_MAX_ENTRIES) {
      // -> Evict the least-recently-used entry (first key in insertion order).
      sanitizeMemo.delete(sanitizeMemo.keys().next().value as string);
    }
    sanitizeMemo.set(html, out);
  }
  return out;
}

// Set an element's innerHTML to the sanitized form of the given HTML.
export function setSafeHtml(el: HTMLElement, html: string): void {
  el.innerHTML = sanitizeSefariaHtml(html);
}
